import { useEffect, useRef, useState } from 'react';
import {
  isTrackReference,
  useLocalParticipant,
  useTracks,
  VideoTrack,
  type TrackReference,
} from '@livekit/components-react';
import { Track, type LocalVideoTrack } from 'livekit-client';
import {
  MEDIA_TRACK_PREFIX,
  audioChannelId,
  isChannelMuted,
  type Crop,
  type SourceManifestEntry,
} from '@liveforge/shared';
import { useDismiss } from '../../hooks/use-dismiss';
import { useLocalSources, type AddSourceInput } from '../../hooks/use-local-sources';
import {
  useMediaSources,
  type MediaSource as MediaSourceState,
} from '../../hooks/use-media-sources';
import { useSendStats } from '../../hooks/use-send-stats';
import { useYoutubeControl } from '../../hooks/use-youtube-control';
import { youtubeLabel, youtubeVideoId } from '../../lib/youtube';
import { listInputDevices, type InputDevices } from '../../lib/media';
import { useStudioStore } from '../../stores/studio-store';
import { AddSourceDialog } from './add-source-dialog';
import { CameraIcon, CloseIcon, MicIcon, PlusIcon, ScreenShareIcon } from './icons';

/**
 * One source, as a director thinks of one: a person, or one of their extra
 * feeds - with whatever audio belongs to it attached.
 */
interface Source {
  key: string;
  who: string;
  what: string;
  local: boolean;
  video: TrackReference | null;
  /** Present when this source carries sound: a microphone, or screen audio. */
  audioChannel: string | null;
  screen: boolean;
  /** Whether the video is actually being published right now. */
  videoLive: boolean;
  /** Set when this is one of the host's own added inputs, so it can be removed. */
  localSourceId: string | null;
}

/**
 * Every source in the room, in one place.
 *
 * There is no separate list of "my inputs": the host's own camera, microphone
 * and capture cards are sources like anyone else's, and having them in two
 * places only raised the question of what the difference was. There isn't one.
 *
 * A person is **one row**, not two. Their camera and their microphone are the
 * same source to a director - you put someone on screen and you open their
 * mic - so the row carries both controls. Extra feeds, a screen share or a
 * second camera, are their own rows, because those are placed independently.
 *
 * Video reaches the audience by being put in a scene; audio never becomes a
 * layer at all, it goes through the programme mix. That is why one control
 * says "Add" and the other says "On air".
 */
export function SourcesDock() {
  const { localParticipant, isScreenShareEnabled } = useLocalParticipant();
  const addLayer = useStudioStore((state) => state.addLayer);
  const setAttachedSources = useStudioStore((state) => state.setAttachedSources);
  const resolvePending = useStudioStore((state) => state.resolvePending);
  const dropPending = useStudioStore((state) => state.dropPending);
  const pendingSources = useStudioStore((state) => state.pendingSources);
  const hydratedFor = useStudioStore((state) => state.hydratedFor);

  const {
    sources: localSources,
    error,
    busy,
    addSource,
    removeSource,
    setVideoEnabled,
    clearError,
  } = useLocalSources();

  const media = useMediaSources();
  const fileInput = useRef<HTMLInputElement | null>(null);
  /** Which stored clip the next file picked is meant to be, if any. */
  const relocating = useRef<{ id: string; index: number } | null>(null);
  /** Device entries already tried, so a failure is not retried every render. */
  const reconnected = useRef(new Set<string>());

  // A player tab that has been opened but not yet captured. Two steps on
  // purpose: the capture dialogue must be raised by a click in *this* tab, and
  // opening the player first takes the focus away.
  const [awaitingCapture, setAwaitingCapture] = useState<{
    videoId: string;
    channelKey: string;
    name: string;
    /** Set when this is a stored clip coming back, not a new one. */
    restore?: { id: string; index: number };
  } | null>(null);

  const [devices, setDevices] = useState<InputDevices>({ video: [], audio: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useDismiss<HTMLDivElement>(menuOpen, () => setMenuOpen(false));

  // Devices are re-read whenever one is plugged or unplugged, so the pickers
  // offer a capture card that appeared after the studio was opened - and stop
  // offering one that has gone.
  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const found = await listInputDevices();
        if (!cancelled) setDevices(found);
      } catch {
        // Enumeration is best-effort: a failure leaves the last known list.
      }
    }

    void refresh();
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener('devicechange', refresh);
    };
  }, []);

  // `Unknown` is where the host's extra multicam sources are published, so
  // leaving it out would hide exactly what Phase 2 exists to allow.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.Microphone, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.ScreenShareAudio, withPlaceholder: false },
      { source: Track.Source.Unknown, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  ).filter(isTrackReference);

  const sources = buildSources(tracks, localSources);

  /**
   * Tells the store what is publishing, so the Live can be saved with it.
   *
   * This runs on every playhead tick - `media.sources` carries a position that
   * moves four times a second - and the store compares by value, so the vast
   * majority of these end in nothing and never reach the debounced save.
   */
  useEffect(() => {
    setAttachedSources([
      ...localSources.map((source) => ({
        id: source.id,
        index: source.index,
        kind: 'device' as const,
        name: source.label,
        videoDeviceId: source.videoDeviceId,
        audioDeviceId: source.audioDeviceId,
        fileName: null,
        videoId: null,
      })),
      ...media.sources.map((clip) => ({
        id: clip.id,
        index: clip.index,
        kind: clip.kind,
        name: clip.name,
        videoDeviceId: null,
        audioDeviceId: null,
        fileName: clip.kind === 'file' ? clip.name : null,
        videoId: clip.videoId,
      })),
    ]);
    // hydratedFor is a dependency, not a value used here: loading a Live
    // clears the attached list, and without this the dock would not say again
    // what it is holding until one of its own lists happened to change.
  }, [localSources, media.sources, setAttachedSources, hydratedFor]);

  /**
   * Cameras come back on their own.
   *
   * A device id survives a reload and so does its permission, so there is
   * nothing to ask the director: the camera that was publishing before the
   * refresh starts publishing again, under the index every scene already
   * points at. A device that is no longer plugged in is left in the list as a
   * row to deal with rather than reacquired into an error.
   */
  useEffect(() => {
    for (const entry of pendingSources) {
      if (entry.kind !== 'device' || !entry.videoDeviceId) continue;
      if (reconnected.current.has(entry.id)) continue;
      if (!devices.video.some((device) => device.deviceId === entry.videoDeviceId)) continue;

      reconnected.current.add(entry.id);
      const input = {
        label: entry.name,
        videoDeviceId: entry.videoDeviceId,
        audioDeviceId: entry.audioDeviceId,
      };
      // Only on success. A camera that refuses - in use by another
      // application, most often - has to stay in the list as a row the
      // director can retry, not vanish from the manifest along with every
      // scene's claim on `cam-N`.
      void addSource(input, { index: entry.index }).then((ok) => {
        if (ok) resolvePending(entry.id);
      });
    }
  }, [pendingSources, devices.video, addSource, resolvePending]);

  async function handleSubmit(input: AddSourceInput) {
    await addSource(input);
    setDialogOpen(false);
  }

  return (
    <div className="sources">
      <div className="dock__header">
        <h2>Sources</h2>
        <div className="source-add" ref={menuRef}>
          <button
            type="button"
            className="dock__add"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <PlusIcon />
            Add
          </button>

          {menuOpen ? (
            <div className="source-add__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  void localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
                }}
              >
                <ScreenShareIcon />
                {isScreenShareEnabled ? 'Stop sharing my screen' : 'Share my screen'}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  clearError();
                  setDialogOpen(true);
                }}
              >
                <CameraIcon />
                Camera or capture card…
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  media.clearError();
                  fileInput.current?.click();
                }}
              >
                <PlusIcon />
                Video file…
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  media.clearError();
                  const pasted = window.prompt('YouTube link or video id');
                  if (!pasted) return;
                  const videoId = youtubeVideoId(pasted);
                  if (!videoId) {
                    window.alert('That is not a YouTube link or video id.');
                    return;
                  }
                  // A tab of its own, and a channel of its own. Several
                  // clips can then be lined up before a show and cut between,
                  // which is what preparing them in advance is for - one shared
                  // player would have them all answering the same command.
                  //
                  // No feature string: passing width or height makes Chrome
                  // open a popup, and the capture picker lists only real tabs
                  // under "Tab" - a popup lands under "Window", which cannot
                  // carry audio.
                  //
                  // It opens in front, and a page cannot help that: a
                  // background tab is an extension privilege
                  // (`chrome.tabs.create({active:false})`), and focusing back
                  // to this tab is ignored as focus stealing.
                  const channelKey = crypto.randomUUID().slice(0, 8);
                  window.open(
                    `/youtube?v=${videoId}&k=${channelKey}`,
                    `liveforge-youtube-${channelKey}`,
                  );
                  setAwaitingCapture({ videoId, channelKey, name: youtubeLabel('', videoId) });
                }}
              >
                <ScreenShareIcon />
                YouTube video…
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="alert alert--warning" role="status">
          {error}
        </p>
      ) : null}

      {/*
        Deliberately narrow: the browser has to decode this, and a container it
        cannot open fails at the file picker rather than after the wait.
      */}
      <input
        ref={fileInput}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = '';
          const target = relocating.current;
          relocating.current = null;
          if (!file) return;
          if (target) {
            // Deliberately not checked against the stored name: the director
            // may well be pointing this clip at a re-encoded or renamed copy,
            // and the studio has no business refusing that.
            void media.addFile(file, target).then((ok) => {
              if (ok) resolvePending(target.id);
            });
            return;
          }
          void media.addFile(file);
        }}
      />

      {media.error ? (
        <p className="alert alert--warning" role="status">
          {media.error}
        </p>
      ) : null}

      {awaitingCapture ? (
        <div className="capture-prompt">
          <p className="muted">
            Come back to this tab, then capture the player: pick the{' '}
            <strong>LiveForge - YOUTUBE</strong> tab in the dialogue and tick{' '}
            <strong>Also share tab audio</strong>. A window or a whole screen gives picture without
            sound.
          </p>
          <div className="capture-prompt__actions">
            <button
              type="button"
              className="control-button"
              disabled={media.busy}
              onClick={() => {
                const pending = awaitingCapture;
                setAwaitingCapture(null);
                void media
                  .addCapture(pending.name, pending.channelKey, pending.videoId, pending.restore)
                  .then((ok) => {
                    // A cancelled picker leaves the clip pending, which is the
                    // truth: nothing was captured.
                    if (ok && pending.restore) resolvePending(pending.restore.id);
                  });
              }}
            >
              Capture the player tab
            </button>
            <button
              type="button"
              className="control-button"
              onClick={() => setAwaitingCapture(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {pendingSources.length > 0 ? (
        <>
          <p className="alert alert--warning" role="status">
            {pendingSources.length === 1
              ? 'One source from the last session has nothing behind it yet.'
              : `${pendingSources.length} sources from the last session have nothing behind them yet.`}{' '}
            Their places in your scenes are kept, and each will fill in as soon as you point it at
            something again.
          </p>
          <ul className="source-list">
            {pendingSources.map((entry) => (
              <PendingRow
                key={entry.id}
                entry={entry}
                busy={busy || media.busy}
                onReattach={() => {
                  if (entry.kind === 'file') {
                    relocating.current = { id: entry.id, index: entry.index };
                    media.clearError();
                    fileInput.current?.click();
                    return;
                  }
                  if (entry.kind === 'youtube' && entry.videoId) {
                    const channelKey = crypto.randomUUID().slice(0, 8);
                    window.open(
                      `/youtube?v=${entry.videoId}&k=${channelKey}`,
                      `liveforge-youtube-${channelKey}`,
                    );
                    setAwaitingCapture({
                      videoId: entry.videoId,
                      channelKey,
                      name: entry.name,
                      restore: { id: entry.id, index: entry.index },
                    });
                    return;
                  }
                  if (entry.kind === 'device' && entry.videoDeviceId) {
                    reconnected.current.add(entry.id);
                    void addSource(
                      {
                        label: entry.name,
                        videoDeviceId: entry.videoDeviceId,
                        audioDeviceId: entry.audioDeviceId,
                      },
                      { index: entry.index },
                    ).then((ok) => {
                      if (ok) resolvePending(entry.id);
                    });
                  }
                }}
                onForget={() => dropPending(entry.id)}
              />
            ))}
          </ul>
        </>
      ) : null}

      {media.sources.length > 0 ? (
        <ul className="source-list">
          {media.sources.map((clip) => (
            <ClipRow
              key={clip.id}
              clip={clip}
              busy={media.busy}
              onPlayFile={(playing) => media.play(clip.id, playing)}
              onSeekFile={(seconds) => media.seek(clip.id, seconds)}
              onLoop={(loop) => media.setLoop(clip.id, loop)}
              onRemove={() => void media.remove(clip.id)}
              onAdd={(name, crop) =>
                addLayer('video', {
                  name,
                  trackSid: clip.videoTrack.sid ?? null,
                  participantIdentity: localParticipant.identity,
                  trackName: `${MEDIA_TRACK_PREFIX}${clip.index}`,
                  crop,
                })
              }
            />
          ))}
        </ul>
      ) : null}

      {sources.length === 0 ? (
        <p className="studio__sidebar-body muted">
          Nothing is being published yet. Sources appear here as people join and turn something on.
        </p>
      ) : (
        <ul className="source-list">
          {sources.map((source) => (
            <li key={source.key} className="source-card">
              <SourceToolbar
                source={source}
                busy={busy}
                onAdd={() => {
                  const video = source.video;
                  if (!video) return;
                  addLayer(source.screen ? 'screenshare' : 'video', {
                    name: `${source.who} - ${source.what}`,
                    // The sid is precise; identity and track name are carried
                    // too, so the layer survives a republish, which changes the
                    // sid but not the pair.
                    trackSid: video.publication.trackSid,
                    participantIdentity: video.participant.identity,
                    ...(source.screen ? {} : { trackName: video.publication.trackName || null }),
                  });
                }}
                onToggleVideo={() => {
                  if (source.screen) {
                    void localParticipant.setScreenShareEnabled(false);
                    return;
                  }
                  if (source.localSourceId) {
                    void setVideoEnabled(source.localSourceId, !source.videoLive);
                    return;
                  }
                  void localParticipant.setCameraEnabled(!source.videoLive);
                }}
                onRemove={
                  source.localSourceId
                    ? () => void removeSource(source.localSourceId ?? '')
                    : undefined
                }
              />

              <div className="source-card__main">
                <div className="source-card__thumb">
                  {source.video ? (
                    <VideoTrack trackRef={source.video} />
                  ) : (
                    <span className="source-card__thumb-empty muted">
                      {source.audioChannel ? <MicIcon on /> : 'no video'}
                    </span>
                  )}
                </div>

                <div className="source-card__meta">
                  <span className="source-card__who" title={source.who}>
                    {source.who}
                    {source.local ? ' (you)' : ''}
                  </span>
                  <span className="source-card__what muted">{source.what}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Mounted only while open, so its form starts empty every time. */}
      {dialogOpen ? (
        <AddSourceDialog
          open
          devices={devices}
          usedVideoDeviceIds={[
            ...localSources.map((source) => source.videoDeviceId),
            // A camera waiting to be reconnected still belongs to that source.
            // Offering it again would put two rows on one device, and the
            // second would take the picture the first is about to reclaim.
            ...pendingSources.flatMap((entry) =>
              entry.videoDeviceId ? [entry.videoDeviceId] : [],
            ),
          ]}
          busy={busy}
          error={error}
          onSubmit={(input) => void handleSubmit(input)}
          onCancel={() => setDialogOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Folds the room's tracks into one row per source.
 *
 * A participant's camera and microphone collapse into a single row; anything
 * else they publish - a screen share, a second angle - stands on its own,
 * because those are placed in a scene independently.
 */
function buildSources(
  tracks: TrackReference[],
  localSources: { id: string; index: number }[],
): Source[] {
  const people = new Map<string, Source>();
  const extras: Source[] = [];

  // The hook numbers its inputs; the published track carries that number in
  // its name, which is the only thing the two have in common on the wire.
  // The hook numbers its inputs; the published track carries that number in
  // its name, which is the only thing the two share once they are on the wire.
  const localByName = new Map<string, string>(
    localSources.map((source) => [`cam-${source.index}`, source.id]),
  );

  for (const reference of tracks) {
    const identity = reference.participant.identity;
    const who = reference.participant.name || identity;
    const local = reference.participant.isLocal;
    const trackName = reference.publication.trackName || '';
    const audio = reference.publication.kind === Track.Kind.Audio;
    const screen =
      reference.source === Track.Source.ScreenShare ||
      reference.source === Track.Source.ScreenShareAudio;

    // Clips have a card of their own, with transport controls; listing them
    // here as well would offer two different ways to drive the same thing.
    if (trackName.startsWith(MEDIA_TRACK_PREFIX)) {
      continue;
    }

    if (screen) {
      const key = `${identity}:screen`;
      const existing = extras.find((entry) => entry.key === key);
      const target =
        existing ??
        (extras.push({
          key,
          who,
          what: 'Screen share',
          local,
          video: null,
          audioChannel: null,
          screen: true,
          videoLive: false,
          localSourceId: null,
        }),
        extras[extras.length - 1]!);

      if (audio) {
        target.audioChannel = audioChannelId(identity, trackName || null);
      } else {
        target.video = reference;
        target.videoLive = !reference.publication.isMuted;
      }
      continue;
    }

    // An extra angle: published under `Unknown` with a name of its own.
    if (reference.source === Track.Source.Unknown) {
      extras.push({
        key: reference.publication.trackSid,
        who,
        what: trackName || 'Extra source',
        local,
        video: audio ? null : reference,
        audioChannel: audio ? audioChannelId(identity, trackName || null) : null,
        screen: false,
        videoLive: !audio && !reference.publication.isMuted,
        localSourceId: localByName.get(trackName) ?? null,
      });
      continue;
    }

    const person = people.get(identity) ?? {
      key: identity,
      who,
      what: 'Camera and microphone',
      local,
      video: null,
      audioChannel: null,
      screen: false,
      videoLive: false,
      localSourceId: null,
    };
    if (audio) {
      person.audioChannel = audioChannelId(identity, trackName || null);
    } else {
      person.video = reference;
      person.videoLive = !reference.publication.isMuted;
    }
    people.set(identity, person);
  }

  // People first, then their extra feeds: the person is what a director looks
  // for, and the extras only mean anything once you have found them.
  return [
    ...[...people.values()].sort((a, b) => a.who.localeCompare(b.who)),
    ...extras.sort((a, b) => a.who.localeCompare(b.who) || a.what.localeCompare(b.what)),
  ];
}

/**
 * The controls for one source, and the two of them mean different things.
 *
 * The microphone is **programme** audio: it decides whether this source is
 * heard by the audience, it touches nobody's device, and it is always
 * reversible. Silencing someone at the source is a different power and lives
 * in the participants tab, worded so the two cannot be confused.
 *
 * The camera button only acts on sources this browser publishes. There is no
 * remote equivalent that is not invasive, so on someone else's feed it reports
 * state and does nothing - a lit icon means their camera is live.
 */
function SourceToolbar({
  source,
  busy,
  onAdd,
  onToggleVideo,
  onRemove,
}: {
  source: Source;
  busy: boolean;
  onAdd: () => void;
  onToggleVideo: () => void;
  onRemove?: () => void;
}) {
  const onAir = useStudioStore((state) =>
    source.audioChannel ? !isChannelMuted(state.audio, source.audioChannel) : false,
  );
  const setChannelMuted = useStudioStore((state) => state.setChannelMuted);

  return (
    <div className="source-card__bar">
      <button
        type="button"
        className={`source-icon ${onAir ? 'source-icon--on' : 'source-icon--off'}`}
        disabled={!source.audioChannel}
        title={
          !source.audioChannel
            ? 'This source carries no audio'
            : onAir
              ? 'On air - click to take this audio off the broadcast'
              : 'Off air - click to put this audio back on the broadcast'
        }
        onClick={() => source.audioChannel && setChannelMuted(source.audioChannel, onAir)}
      >
        <MicIcon on={onAir} />
      </button>

      <button
        type="button"
        className={`source-icon ${source.videoLive ? 'source-icon--on' : 'source-icon--off'}`}
        disabled={!source.video || !source.local || busy}
        title={
          !source.video
            ? 'This source carries no video'
            : !source.local
              ? source.videoLive
                ? 'Their camera is live'
                : 'Their camera is off'
              : source.videoLive
                ? 'Stop publishing this video'
                : 'Publish this video again'
        }
        onClick={onToggleVideo}
      >
        <CameraIcon on={source.videoLive} />
      </button>

      <button
        type="button"
        className="source-icon source-icon--add"
        disabled={!source.video}
        title={
          source.video ? 'Add this source to the scene being edited' : 'There is no video to place'
        }
        onClick={onAdd}
      >
        <PlusIcon />
      </button>

      {onRemove ? (
        <button
          type="button"
          className="source-icon source-icon--remove"
          disabled={busy}
          title="Stop publishing this input"
          onClick={onRemove}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
}

/** mm:ss, which is the only form a transport bar ever needs. */
function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * Shows the clip's own decoder rather than a second one.
 *
 * The element already exists and is already playing - it is what feeds the
 * published track - so the card borrows it instead of opening the file again.
 * Two decoders would be two pictures that could drift, and twice the work for
 * the same frames.
 */
function ClipPreview({ element }: { element: HTMLVideoElement | null }) {
  const host = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const parent = host.current;
    if (!parent || !element) return;
    parent.appendChild(element);
    // Removed, never stopped: playback continues while the card is on another
    // tab, because the room is still being fed from it.
    return () => {
      element.remove();
    };
  }, [element]);

  return <div className="source-card__thumb" ref={host} />;
}

/**
 * A clip, with the controls that drive it.
 *
 * Transport belongs to the source and not to a layer: the same clip can sit in
 * two scenes, and it would make no sense for it to have two positions.
 */
/**
 * What the publication is managing to send, under the clip's own name.
 *
 * A clip that stutters is either more than the uplink can carry or more than
 * this machine can encode, and the two need opposite fixes. WebRTC knows which
 * and has always known; this is only the act of showing it to the person
 * watching the clip stutter.
 */
function ClipHealth({ track }: { track: LocalVideoTrack }) {
  const stats = useSendStats(track);
  if (!stats) return null;

  const limited = stats.limitedBy !== 'none';
  const lossy = stats.lostPackets > 0;
  return (
    <span
      className={`source-card__health ${limited || lossy ? 'source-card__health--warn' : ''}`}
      title={
        `${stats.kbps} kbps - ${stats.fps} fps - ${stats.size} - ` +
        `limited by ${stats.limitedBy}` +
        (lossy ? ` - ${stats.lostPackets} packets lost` : '') +
        (stats.rtt !== null ? ` - rtt ${stats.rtt} ms` : '')
      }
    >
      {(stats.kbps / 1000).toFixed(1)}M · {stats.fps}fps
      {limited ? ` · ${stats.limitedBy}` : ''}
      {lossy ? ` · ${stats.lostPackets} lost` : ''}
    </span>
  );
}

function MediaCard({
  clip,
  busy,
  onPlay,
  onSeek,
  onLoop,
  onRemove,
  onAdd,
}: {
  clip: MediaSourceState;
  busy: boolean;
  onPlay: (playing: boolean) => void;
  onSeek: (seconds: number) => void;
  onLoop: (loop: boolean) => void;
  onRemove: () => void;
  onAdd: () => void;
}) {
  return (
    <li className="source-card source-card--media">
      <div className="source-card__bar">
        <button
          type="button"
          className={`source-icon ${clip.playing ? 'source-icon--on' : 'source-icon--off'}`}
          title={clip.playing ? 'Pause' : 'Play'}
          onClick={() => onPlay(!clip.playing)}
        >
          {clip.playing ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          className={`source-icon ${clip.loop ? 'source-icon--on' : ''}`}
          title={clip.loop ? 'Stop looping' : 'Loop this clip'}
          onClick={() => onLoop(!clip.loop)}
        >
          ↻
        </button>
        <button
          type="button"
          className="source-icon source-icon--add"
          title="Add this clip to the scene being edited"
          onClick={onAdd}
        >
          <PlusIcon />
        </button>
        <button
          type="button"
          className="source-icon source-icon--remove"
          disabled={busy}
          title="Stop playing this file"
          onClick={onRemove}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="source-card__main">
        <ClipPreview element={clip.preview} />

        <div className="source-card__meta">
          <span className="source-card__who" title={clip.name}>
            {clip.name}
          </span>
          <span className="source-card__what muted">
            {clock(clip.position)} / {clock(clip.duration)}
          </span>
          <ClipHealth track={clip.videoTrack} />
        </div>

        <input
          className="clip-seek"
          type="range"
          min={0}
          max={Math.max(clip.duration, 0.1)}
          step={0.1}
          value={Math.min(clip.position, clip.duration || 0)}
          aria-label="Playback position"
          onChange={(event) => onSeek(Number.parseFloat(event.target.value))}
        />
      </div>
    </li>
  );
}

/**
 * One clip in the list, holding its own connection to its own player.
 *
 * The hook lives here rather than in the dock because each YouTube clip has a
 * channel of its own: one per row is what lets several be prepared before a
 * show and cut between, instead of every open player answering the same
 * command. A file passes a null key and opens no channel at all.
 */
function ClipRow({
  clip,
  busy,
  onPlayFile,
  onSeekFile,
  onLoop,
  onRemove,
  onAdd,
}: {
  clip: MediaSourceState;
  busy: boolean;
  onPlayFile: (playing: boolean) => void;
  onSeekFile: (seconds: number) => void;
  onLoop: (loop: boolean) => void;
  onRemove: () => void;
  onAdd: (name: string, crop: Crop | null) => void;
}) {
  const youtube = useYoutubeControl(clip.kind === 'youtube' ? clip.channelKey : null);
  const remote = clip.kind === 'youtube' ? youtube.state : null;
  const setMediaLabel = useStudioStore((state) => state.setMediaLabel);

  // A capture has no element to ask, so its player's own report is what the
  // card shows - and the reported title is what the clip is called.
  const shown = remote
    ? {
        ...clip,
        name: youtubeLabel(remote.title, remote.videoId),
        playing: remote.playing,
        position: remote.position,
        duration: remote.duration,
      }
    : clip;

  // The track picker in the properties panel sees only `media-1`; this is
  // where the human name for it comes from.
  useEffect(() => {
    setMediaLabel(`${MEDIA_TRACK_PREFIX}${clip.index}`, shown.name);
  }, [clip.index, shown.name, setMediaLabel]);

  return (
    <MediaCard
      clip={shown}
      busy={busy}
      onPlay={(playing) => {
        if (clip.kind === 'youtube') {
          youtube.send({ type: playing ? 'play' : 'pause' });
          return;
        }
        onPlayFile(playing);
      }}
      onSeek={(seconds) => {
        if (clip.kind === 'youtube') {
          youtube.send({ type: 'seek', seconds });
          return;
        }
        onSeekFile(seconds);
      }}
      onLoop={onLoop}
      onRemove={onRemove}
      onAdd={() =>
        onAdd(
          shown.name,
          // The capture is the whole tab; the player reports where its 16:9
          // box sits inside it, and that becomes the crop.
          remote?.frame ?? null,
        )
      }
    />
  );
}

/**
 * A source the Live remembers and this session has not filled in yet.
 *
 * It is a row rather than a warning, because it is still a source: it holds
 * its track index, so every scene that placed it stays pointed at the right
 * feed and draws the renderer's placeholder until a picture arrives. What
 * differs by kind is only how much the browser is allowed to do unaided - a
 * camera comes back by itself, a file cannot come back at all without being
 * pointed at again.
 */
function PendingRow({
  entry,
  busy,
  onReattach,
  onForget,
}: {
  entry: SourceManifestEntry;
  busy: boolean;
  onReattach: () => void;
  onForget: () => void;
}) {
  const track =
    entry.kind === 'device' ? `cam-${entry.index}` : `${MEDIA_TRACK_PREFIX}${entry.index}`;

  const [action, why] =
    entry.kind === 'file'
      ? ['Locate the file…', 'The file is not open any more - a page cannot keep one.']
      : entry.kind === 'youtube'
        ? ['Reopen and capture', 'The player tab closed with the studio.']
        : entry.videoDeviceId
          ? ['Reconnect', 'This camera is not plugged in, or is in use elsewhere.']
          : ['Reconnect', 'This source has no device recorded.'];

  return (
    <li className="source-card source-card--pending">
      <div className="source-card__main">
        <div className="source-card__thumb">
          <span className="source-card__thumb-empty muted">no signal</span>
        </div>
        <div className="source-card__meta">
          <span className="source-card__who" title={entry.name}>
            {entry.name}
          </span>
          <span className="source-card__what muted">
            <code>{track}</code> · {why}
          </span>
        </div>
      </div>
      <div className="source-card__pending-actions">
        <button type="button" className="control-button" disabled={busy} onClick={onReattach}>
          {action}
        </button>
        <button
          type="button"
          className="source-icon"
          title="Forget this source"
          aria-label={`Forget ${entry.name}`}
          onClick={onForget}
        >
          <CloseIcon />
        </button>
      </div>
    </li>
  );
}
